'use client'

import { FC } from 'react'
import { testIds } from '@/root/shared-consts'
import { FieldErrors, FieldValues, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { useGetTranslations } from '@/root/shared-hooks'
import PersonForm from './PersonForm'
import CompanyForm from './CompanyForm'
import RoleSelector from './RoleSelector'
import {
  useCustomerInfoView,
  type CustomerType,
  type FieldConfig,
  type RoleOption,
} from '@/root/modules/shared/ConfirmPage'

interface CustomerInfoProps {
  companyForm?: boolean
  privatePersonForm?: boolean
  // Form props from parent
  register: UseFormRegister<FieldValues>
  setValue: UseFormSetValue<FieldValues>
  watch: UseFormWatch<FieldValues>
  errors: FieldErrors<FieldValues>
  clearErrors: (name?: string) => void
  // Field configurations
  personFields: FieldConfig[]
  companyFields: FieldConfig[]
  // Customer type
  customerType: CustomerType
  setCustomerType: (type: CustomerType) => void
  // Save on blur
  onBlur: () => void
  // Auth & role selector props
  isAuth?: boolean
  roleOptions?: RoleOption[]
  selectedRole?: string
  selectedRoleType?: CustomerType
  onRoleChange?: (option: RoleOption) => void
  isProfileLoading?: boolean
}

export const CustomerInfo: FC<CustomerInfoProps> = ({
  companyForm,
  privatePersonForm,
  register,
  setValue,
  watch,
  errors,
  clearErrors,
  personFields,
  companyFields,
  customerType,
  setCustomerType,
  onBlur,
  isAuth = false,
  roleOptions = [],
  selectedRole,
  selectedRoleType,
  onRoleChange,
  isProfileLoading,
}) => {
  const { getTranslation } = useGetTranslations()

  const {
    showBothOptions,
    isProfileCompanySelected,
    isCompanyExpanded,
    handleToggleCompany,
    showCompanyForm,
    showCompanyToggle,
  } = useCustomerInfoView({
    companyForm,
    privatePersonForm,
    isAuth,
    customerType,
    selectedRoleType,
    setValue,
    clearErrors,
  })

  return (
    <div className="flex w-full max-w-full flex-col gap-5 lg:max-w-sm">
      <h2 className="text-2xl leading-tight font-bold">
        {getTranslation('confirmationContactFormCustomerInformation')}
      </h2>

      {/* Customer type selector */}
      {showBothOptions && (
        <RoleSelector
          isAuth={isAuth}
          roleOptions={roleOptions}
          selectedRole={selectedRole ?? ''}
          onRoleChange={onRoleChange ?? (() => {})}
          isLoading={isProfileLoading}
          customerType={customerType}
          setCustomerType={setCustomerType}
        />
      )}

      {/* Person form - always visible */}
      {privatePersonForm && (
        <PersonForm
          register={register}
          setValue={setValue}
          errors={errors}
          clearErrors={clearErrors}
          fields={personFields}
          onBlur={onBlur}
        />
      )}

      {/* Company form */}
      {showCompanyForm && (
        <CompanyForm
          register={register}
          setValue={setValue}
          watch={watch}
          errors={errors}
          clearErrors={clearErrors}
          fields={companyFields}
          onBlur={onBlur}
          readOnly={isProfileCompanySelected}
        />
      )}

      {/* Toggle button for company form — only for auth users in private mode (guests use radio buttons) */}
      {showCompanyToggle && (
        <button
          type="button"
          onClick={handleToggleCompany}
          data-testid={testIds.confirm.companyDetailsToggle}
          className="text-primary-base self-start text-sm font-medium underline underline-offset-2"
        >
          {isCompanyExpanded
            ? getTranslation('confirmPageRemoveCompanyDetails')
            : getTranslation('confirmPageAddCompanyDetails')}
        </button>
      )}
    </div>
  )
}
